import { FC, useState, useRef, useId } from "react";

import { Box, Stack } from "@mui/material";
import {
  DEFAULT_DAF_PROVIDERS,
  DafProvider,
  getDafProvider,
  OTHER_DAF_PROVIDER,
  searchDafProviders,
} from "@simplyk/common";

import { useTranslate } from "@/common/hooks/useTranslate";
import { FormButton } from "@/forms-design-system/FormButton";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { ProviderLogo } from "./ProviderLogo/ProviderLogo";
import { ProviderSearch } from "./ProviderSearch/ProviderSearch";

interface ProviderPickerProps {
  value?: string;
  onChange: (providerId: string) => void;
  error?: string;
}

export const ProviderPicker: FC<ProviderPickerProps> = ({ value, onChange, error }) => {
  const { t } = useTranslate();
  const rootRef = useRef<HTMLDivElement>(null);
  const resultsId = useId();
  const [search, setSearch] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const selectedProvider = getDafProvider(value);
  const tiles =
    selectedProvider && !selectedProvider.dafDirect
      ? [selectedProvider, ...DEFAULT_DAF_PROVIDERS]
      : DEFAULT_DAF_PROVIDERS;
  const matches = searchDafProviders(search);
  const label = (provider: DafProvider) =>
    provider.id === OTHER_DAF_PROVIDER.id ? t("common", "daf.other") : provider.name;
  const select = (providerId: string) => {
    onChange(providerId);
    setSearch("");
    setIsSearchOpen(false);
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <Stack
      gap={1}
      data-test="daf-provider-picker"
      ref={rootRef}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setSearch("");
          setIsSearchOpen(false);
        }
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.stopPropagation();
          setSearch("");
          rootRef.current?.querySelector("input")?.focus();
          setIsSearchOpen(false);
          return;
        }
        if (event.key === "Enter") {
          event.preventDefault();
          event.stopPropagation();
          const target = event.target as HTMLElement;
          if (target.closest("button")) {
            target.closest("button")?.click();
          } else if (isSearchOpen) {
            select(!search.trim() && value ? value : (matches[0]?.id ?? OTHER_DAF_PROVIDER.id));
          }
          return;
        }
        if (!isSearchOpen || !["ArrowDown", "ArrowUp"].includes(event.key)) {
          return;
        }
        const options = Array.from(rootRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]') ?? []);
        if (!options.length) {
          return;
        }
        event.preventDefault();
        const currentIndex = options.indexOf(document.activeElement as HTMLButtonElement);
        const nextIndex =
          event.key === "ArrowDown"
            ? (currentIndex + 1) % options.length
            : currentIndex <= 0
              ? options.length - 1
              : currentIndex - 1;
        options[nextIndex]?.focus();
      }}
    >
      <Box sx={{ position: "relative" }}>
        <ProviderSearch
          value={search}
          onChange={(value) => {
            setSearch(value);
            setIsSearchOpen(true);
          }}
          onFocus={() => setIsSearchOpen(true)}
          isOpen={isSearchOpen}
          resultsId={resultsId}
        />
        {isSearchOpen && (
          <Stack
            id={resultsId}
            role="listbox"
            aria-label={t("common", "daf.searchProviders")}
            sx={(theme) => ({
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              zIndex: 10,
              maxHeight: theme.spacing(38),
              overflowY: "auto",
              py: 1,
              paddingTop: `calc(${theme.spacing(1)} - 1px)`,
              borderRadius: theme.radius(1),
              backgroundColor: theme.palette.forms.surface.supershy,
              boxShadow: theme.boxShadow.base.strong,
              borderTop: `1px solid ${theme.palette.forms.border.supershy}`,
            })}
          >
            {matches.length === 0 && (
              <Stack direction="row" alignItems="center" gap={1} paddingX={2} paddingY={1}>
                <Box
                  sx={(theme) => ({
                    display: "flex",
                    p: 0.5,
                    borderRadius: theme.radius(0.5),
                    backgroundColor: theme.palette.forms.surface.moderate,
                    color: theme.palette.forms.text.brand,
                  })}
                >
                  <Box
                    component="span"
                    sx={(theme) => ({
                      width: theme.spacing(2),
                      height: theme.spacing(2),
                      backgroundColor: "currentColor",
                      maskImage: "url(/assets/daf-provider-info.svg)",
                      maskSize: "contain",
                    })}
                  />
                </Box>
                <FormTypography variant="body2" vibe="moderate">
                  {t("common", "daf.noKnownProvider", { search })}
                </FormTypography>
              </Stack>
            )}
            {[...matches.slice(0, 30), OTHER_DAF_PROVIDER].map((provider) => (
              <FormButton
                key={provider.id}
                variant="ghost"
                role="option"
                aria-selected={provider.id === value}
                onClick={() => select(provider.id)}
                sx={(theme) => ({
                  ...theme.typography.body2,
                  borderRadius: 0,
                  padding: theme.spacing(1, 2),
                  minHeight: theme.spacing(4.5),
                  "&:hover, &:focus-visible": { backgroundColor: theme.palette.forms.surface.quiet },
                  justifyContent: "flex-start",
                  flexShrink: 0,
                  whiteSpace: "normal",
                  height: "auto",
                  textAlign: "start",
                })}
              >
                {label(provider)}
              </FormButton>
            ))}
            {matches.length > 30 && (
              <FormTypography variant="body2" vibe="moderate" sx={{ px: 2, py: 1 }}>
                {t("common", "daf.refineSearch", { count: matches.length - 30 })}
              </FormTypography>
            )}
          </Stack>
        )}
      </Box>
      <Stack direction="row" gap={1.5} flexWrap="wrap" role="radiogroup" aria-label={t("common", "daf.selectProvider")}>
        {tiles.map((provider) => (
          <FormButton
            key={provider.id}
            variant={provider.id === value ? "solid" : "outline"}
            role="radio"
            aria-checked={provider.id === value}
            data-test={`daf-provider-${provider.id}`}
            onClick={() => select(provider.id)}
            sx={(theme) => ({
              flex: `1 1 calc((100% - ${theme.spacing((tiles.length - 1) * 1.5)}) / ${tiles.length})`,
              minWidth: theme.spacing(16),
              minHeight: theme.spacing(13),
              padding: `calc(${theme.spacing(1.5)} - 1px)`,
              borderRadius: theme.radius(0.625),
              backgroundColor:
                provider.id === value
                  ? theme.palette.formsComponents.button.solid.backgroundColor
                  : theme.palette.formsComponents.paymentMethod.institutionOptionBackgroundColor,
              border: "1px solid",
              borderColor:
                provider.id === value
                  ? "transparent"
                  : theme.palette.formsComponents.paymentMethod.institutionOptionBorderColor,
              color:
                provider.id === value
                  ? theme.palette.formsComponents.button.solid.color
                  : theme.palette.formsComponents.paymentMethod.institutionOptionTextColor,
              boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.02), 0px 1px 1px rgba(0, 0, 0, 0.03)",
              whiteSpace: "normal",
              height: "auto",
              alignSelf: "stretch",
              alignItems: "flex-start",
              "& > .MuiStack-root": { minWidth: 0, width: "100%" },
            })}
          >
            <Stack gap={1} alignItems="center" width="100%">
              <ProviderLogo provider={provider} />
              <FormTypography
                variant="body1"
                textAlign="center"
                sx={{ width: "100%", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {label(provider)}
              </FormTypography>
            </Stack>
          </FormButton>
        ))}
      </Stack>
      <FormTypography variant="body2" vibe="quiet" data-test="daf-provider-next-step">
        {t("common", "daf.confirmWithProviderNextStep")}
      </FormTypography>
      {error && (
        <FormTypography role="alert" vibe="error" variant="body2">
          {error}
        </FormTypography>
      )}
    </Stack>
  );
};
